import { useEffect, useRef, useState } from "react";
import { Link } from "wouter";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Bell, BellRing, Check, X, Settings } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { apiRequest } from "@/lib/queryClient";
import { formatDistanceToNow } from "date-fns";
import { cn } from "@/lib/utils";

interface NotificationRow {
  id: string;
  category: string;
  title: string;
  body: string | null;
  linkUrl: string | null;
  severity: "info" | "success" | "warning" | "error";
  readAt: string | null;
  dismissedAt: string | null;
  createdAt: string;
}

interface UnreadCountResp {
  count: number;
}

interface ListResp {
  items: NotificationRow[];
  nextCursor: string | null;
}

export const severityDot: Record<NotificationRow["severity"], string> = {
  info: "bg-blue-500",
  success: "bg-green-500",
  warning: "bg-amber-500",
  error: "bg-red-500",
};

export type { NotificationRow };

export function NotificationBell() {
  const [open, setOpen] = useState(false);
  const qc = useQueryClient();
  const autoReadTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const { data: unread } = useQuery<UnreadCountResp>({
    queryKey: ["/api/notifications/unread-count"],
    refetchInterval: 30_000,
  });

  const { data: list, isLoading } = useQuery<ListResp>({
    queryKey: ["/api/notifications", { limit: 20 }],
    enabled: open,
    queryFn: async () => {
      const res = await fetch("/api/notifications?limit=20", {
        credentials: "include",
      });
      if (!res.ok) throw new Error(`Failed to load notifications (${res.status})`);
      return res.json();
    },
  });

  const markAllRead = useMutation({
    mutationFn: () => apiRequest("POST", "/api/notifications/read-all", {}),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["/api/notifications/unread-count"] });
      qc.invalidateQueries({ queryKey: ["/api/notifications"] });
    },
  });

  const markRead = useMutation({
    mutationFn: (id: string) =>
      apiRequest("POST", `/api/notifications/${id}/read`, {}),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["/api/notifications/unread-count"] });
      qc.invalidateQueries({ queryKey: ["/api/notifications"] });
    },
  });

  const dismiss = useMutation({
    mutationFn: (id: string) =>
      apiRequest("POST", `/api/notifications/${id}/dismiss`, {}),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["/api/notifications/unread-count"] });
      qc.invalidateQueries({ queryKey: ["/api/notifications"] });
    },
  });

  const count = unread?.count ?? 0;
  const display = count > 99 ? "99+" : String(count);

  useEffect(() => {
    if (!open) return;
    qc.invalidateQueries({ queryKey: ["/api/notifications"] });
  }, [open, qc]);

  // Auto-mark-read after 2 seconds of the popover being open
  useEffect(() => {
    if (autoReadTimerRef.current) {
      clearTimeout(autoReadTimerRef.current);
      autoReadTimerRef.current = null;
    }
    if (open && count > 0) {
      autoReadTimerRef.current = setTimeout(() => {
        markAllRead.mutate();
      }, 2000);
    }
    return () => {
      if (autoReadTimerRef.current) {
        clearTimeout(autoReadTimerRef.current);
        autoReadTimerRef.current = null;
      }
    };
  }, [open, count]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="relative h-9 w-9"
          data-testid="button-notifications-bell"
          aria-label={`Notifications${count ? ` — ${count} unread` : ""}`}
        >
          {count > 0 ? (
            <BellRing className="h-5 w-5" />
          ) : (
            <Bell className="h-5 w-5" />
          )}
          {count > 0 && (
            <Badge
              variant="destructive"
              className="absolute -top-0.5 -right-0.5 h-4 min-w-[1rem] px-1 text-[10px] leading-none rounded-full flex items-center justify-center"
              data-testid="badge-notifications-unread"
            >
              {display}
            </Badge>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        className="w-[380px] p-0"
        data-testid="popover-notifications"
      >
        <div className="flex items-center justify-between px-3 py-2 border-b">
          <div className="font-semibold text-sm">Notifications</div>
          <div className="flex items-center gap-1">
            {count > 0 && (
              <Button
                variant="ghost"
                size="sm"
                className="h-7 px-2 text-xs"
                onClick={() => markAllRead.mutate()}
                disabled={markAllRead.isPending}
                data-testid="button-notifications-mark-all-read"
              >
                <Check className="h-3 w-3 mr-1" />
                Mark all read
              </Button>
            )}
            <Link href="/settings/notifications">
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7"
                onClick={() => setOpen(false)}
                data-testid="button-notifications-settings"
                aria-label="Notification settings"
              >
                <Settings className="h-3.5 w-3.5" />
              </Button>
            </Link>
          </div>
        </div>

        <ScrollArea className="max-h-[480px]">
          {isLoading && (
            <div className="px-3 py-8 text-center text-sm text-muted-foreground">
              Loading…
            </div>
          )}
          {!isLoading && (!list?.items || list.items.length === 0) && (
            <div className="px-3 py-10 text-center text-sm text-muted-foreground">
              You're all caught up.
            </div>
          )}
          {list?.items?.map((n) => {
            const isUnread = !n.readAt;
            const inner = (
              <div
                className={cn(
                  "px-3 py-2.5 border-b last:border-b-0 flex gap-2 items-start group hover:bg-accent/40 transition-colors",
                  isUnread && "bg-accent/20"
                )}
                data-testid={`notification-item-${n.id}`}
              >
                <span
                  className={cn(
                    "mt-1.5 h-2 w-2 rounded-full shrink-0",
                    severityDot[n.severity]
                  )}
                  aria-hidden
                />
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium leading-snug truncate">
                    {n.title}
                  </div>
                  {n.body && (
                    <div className="text-xs text-muted-foreground mt-0.5 line-clamp-2">
                      {n.body}
                    </div>
                  )}
                  <div className="text-[10px] text-muted-foreground mt-1">
                    {formatDistanceToNow(new Date(n.createdAt), {
                      addSuffix: true,
                    })}
                  </div>
                </div>
                <div className="flex flex-col gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  {isUnread && (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        markRead.mutate(n.id);
                      }}
                      data-testid={`button-notification-read-${n.id}`}
                      aria-label="Mark read"
                    >
                      <Check className="h-3 w-3" />
                    </Button>
                  )}
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      dismiss.mutate(n.id);
                    }}
                    data-testid={`button-notification-dismiss-${n.id}`}
                    aria-label="Dismiss"
                  >
                    <X className="h-3 w-3" />
                  </Button>
                </div>
              </div>
            );
            if (n.linkUrl) {
              return (
                <Link
                  key={n.id}
                  href={n.linkUrl}
                  onClick={() => {
                    if (isUnread) markRead.mutate(n.id);
                    setOpen(false);
                  }}
                >
                  {inner}
                </Link>
              );
            }
            return <div key={n.id}>{inner}</div>;
          })}
        </ScrollArea>

        <div className="border-t px-3 py-2 text-center">
          <Link href="/notifications">
            <Button
              variant="ghost"
              size="sm"
              className="text-xs h-7"
              onClick={() => setOpen(false)}
              data-testid="link-notifications-see-all"
            >
              See All
            </Button>
          </Link>
        </div>
      </PopoverContent>
    </Popover>
  );
}
